import { trackedObject } from '@ember/reactive/collections';
import Service, { type Registry as Services, service } from '@ember/service';
import BaseActionPollerService from 'lumen/services/base-action-poller';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RestrictionAction } from 'lumen/services/repositories/restriction-action';
import { ABSTRACT_ACTIONS } from 'lumen/utils/constants/restriction-actions';
import { logMessage } from 'lumen/utils/log-sentry-breadcrumb';

type SubscriptionCallbackFunction = (
  date: string,
  subscriptionId: string,
) => Promise<void>;

export default class RestrictionsActionPollerService extends Service {
  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare store: Services['store'];

  declare baseActionPoller: BaseActionPollerService<RestrictionAction>;

  datesBeingRefreshed = trackedObject<Record<string, boolean>>();
  logMessage = logMessage;
  onActionInEndStateSubscription: SubscriptionCallbackFunction | null = null;

  /**
   * Provides the subscriptionId for the poller.
   * Note: Named subscriptionId for API consistency with the decorator
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subscriptionId() {
    // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
    return parseInt(this.hotelAndMarketService.hotel?.id, 10);
  }

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);

    // Config Object
    this.baseActionPoller = new BaseActionPollerService<RestrictionAction>(
      {
        typeKey: 'pricing-assistant/restrictions/recommendations/action/status',
        actionModelName: 'restriction-action',
        interval: 2000,
        onActionInEndState: this.onActionInEndState.bind(this),
        onPollingFinished: this.onPollingFinished.bind(this),
      },
      this.store,
    );
    this.baseActionPoller.subscriptionId = this.subscriptionId;
  }

  /**
   * Function called on each test on action on the API responses
   * Informs about each action that "enters" the state `PUSHED` or `EXCEPTION`
   */
  /**
   * Add a recommendation to the service array to poll for latest recommendationAction statuses
   *
   * @param restrictionActions    Array of actions to be in the push/pull flow
   */

  addActionsToFollow(restrictionActions: RestrictionAction[]): void {
    if (!this || !this.baseActionPoller) {
      console.error(
        'BaseActionPollerService was not defined, or JS context of the caller is wrongly set',
      );
      return;
    }

    const filteredRestrictionActions = restrictionActions.filter(
      (restrictionAction) =>
        restrictionAction.action === ABSTRACT_ACTIONS.ACCEPTED ||
        restrictionAction.action === ABSTRACT_ACTIONS.PUSHING,
    );

    filteredRestrictionActions.forEach((restrictionAction) => {
      const fromDate = restrictionAction.fromDate?.format('YYYY-MM-DD');
      this.addTrackingActionsStatus(fromDate);
    });

    this.baseActionPoller.addActionsToFollow(filteredRestrictionActions);
  }

  // SMART DISTRIBUTION TODO: are these methods actually useful right now? Can currently be handled in oneliners
  addTrackingActionsStatus(key: string): void {
    this.datesBeingRefreshed[key] = true;
  }

  async onActionInEndState(
    actionType: string,
    restrictionAction: RestrictionAction,
  ): Promise<void> {
    this.logMessage(
      {
        type: 'debug',
        message: `restriction-action-poller: onActionInEndState: ${actionType}`,
      },
      this.errorTracking,
      this.access.isStaffUser,
    );

    if (
      actionType === ABSTRACT_ACTIONS.PUSHED ||
      actionType === ABSTRACT_ACTIONS.EXCEPTION
    ) {
      if (this.onActionInEndStateSubscription) {
        await this.onActionInEndStateSubscription(
          restrictionAction.fromDate.format('YYYY-MM-DD'),
          this.subscriptionId.toString(),
        );
      }
    }

    this.removeTrackingActionsStatus(
      restrictionAction.fromDate.format('YYYY-MM-DD'),
    );
  }

  /**
   * Function called after all actions that are present in an API response are tested.
   */

  onPollingFinished(): void {
    this.logMessage(
      {
        type: 'debug',
        message: 'restriction-action-poller: onPollingFinished',
      },
      this.errorTracking,
      this.access.isStaffUser,
    );
  }

  /**
   * Removes a recommendation from the service array to poll for latest recommendationAction statuses
   */

  removeRecommendationToFollow(restrictionAction: RestrictionAction): void {
    this.baseActionPoller.removeActionToFollow(restrictionAction);
  }

  removeTrackingActionsStatus(key: string): void {
    delete this.datesBeingRefreshed[key];
  }

  /**
   * Function that aborts polling and cleans all its state
   */

  stopPolling(): void {
    this.logMessage(
      {
        type: 'debug',
        message: 'restriction-action-poller: stopPolling',
      },
      this.errorTracking,
      this.access.isStaffUser,
    );

    this.baseActionPoller.stopPolling();
  }
}

declare module '@ember/service' {
  interface Registry {
    'restrictions/action-poller': RestrictionsActionPollerService;
  }
}
